import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import secureApiClient from '../utils/secureApiClient';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

import { STEPS, initialForm, Progress, StepPersonal, StepEmergency, StepInsurance, StepClinical, StepConsents } from '../components/IntakeQuestionnaire';

// ─── Main component ───────────────────────────────────────────────────────────

export default function ClientIntakeForm() {
  const { currentUser, refreshCurrentUser } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Preserve saved answers when clients return to finish or update intake.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const u = userSnap.data() || {};
        const clinicalSnap = u.clinicalId
          ? await getDoc(doc(db, 'clinicalRecords', u.clinicalId)) : null;
        const c = clinicalSnap?.data() || {};
        const address = u.address || {};
        const emergency = u.emergencyContact || {};
        const insurance = u.insuranceDetails || u.insurance || {};
        const saved = { ...u, ...c,
          paymentPreference: u.paymentPreference ?? insurance.paymentOption,
          street: address.street ?? u.street, city: address.city ?? u.city,
          state: address.state ?? u.state, zip: address.zip ?? address.zipCode ?? u.zip,
          dateOfBirth: u.dateOfBirth ?? u.birthDate,
          emergencyContactName: emergency.name ?? u.emergencyContactName,
          emergencyContactPhone: emergency.phone ?? u.emergencyContactPhone,
          medicationsCurrent: c.medications?.current ?? c.medicationsCurrent ?? false,
          medicationsList: c.medications?.list ?? c.medicationsList,
          consentInformedConsent: u.consentSigned?.informedConsent === true,
          consentCancellationPolicy: u.consentSigned?.cancellationPolicy === true,
          consentTelehealth: u.consentSigned?.telehealthConsent === true,
        };
        for (const [field, key] of Object.entries({ insuranceProvider: 'provider',
          insurancePlanName: 'planName', insuranceMemberId: 'memberId', insuranceGroupId: 'groupId',
          insurancePhone: 'phone', deductible: 'deductible', copay: 'copay', oopMax: 'oopMax' })) {
          saved[field] = insurance[key] ?? u[field];
        }
        if (!cancelled) setForm(Object.fromEntries(Object.entries(initialForm)
          .map(([key, fallback]) => [key, saved[key] ?? fallback])));
      } catch {
        if (!cancelled) { setLoadError(true); setError('Unable to load your saved intake. Please reload before continuing.'); }
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [currentUser.uid]);

  const handle = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // ─── Per-step validation ────────────────────────────────────────────────────
  const validate = (step = null) => {
    const answered = value => typeof value === 'string' ? value.trim().length > 0 : typeof value === 'boolean' ? value : value !== '' && value !== null && value !== undefined;
    if (step === null || step === 0) {
      if (!answered(form.preferredName)) return 'Preferred name is required.';
      if (!answered(form.phone)) return 'Phone number is required.';
      if (!answered(form.dateOfBirth)) return 'Date of birth is required.';
      if (!answered(form.street) || !answered(form.city) || !answered(form.state) || !answered(form.zip))
        return 'Full address is required.';
      if (!answered(form.employerOrSchool)) return 'Employer / school is required.';
      if (!answered(form.paymentPreference)) return 'Please select a payment preference.';
    }
    if (step === null || step === 1) {
      if (!answered(form.emergencyContactName)) return 'Emergency contact name is required.';
      if (!answered(form.emergencyContactPhone)) return 'Emergency contact phone is required.';
    }
    if (step === null || step === 2) {
      if (!answered(form.insuranceType)) return 'Please select an insurance category.';
      if (form.insuranceType === 'private' || form.insuranceType === 'medicaid') {
        if (!answered(form.insuranceProvider)) return 'Please select an insurance provider.';
        if (!answered(form.insurancePlanName)) return 'Insurance plan name is required.';
        if (!answered(form.insuranceMemberId)) return 'Member ID / policy # is required.';
        if (!answered(form.insuranceGroupId)) return 'Group number is required.';
        if (!answered(form.insurancePhone)) return 'Insurance phone is required.';
        if (!answered(form.deductible)) return 'Deductible is required.';
        if (!answered(form.copay)) return 'Copay is required.';
        if (!answered(form.oopMax)) return 'Out-of-pocket max is required.';
      }
    }
    if (step === null || step === 3) {
      if (!answered(form.maritalStatus)) return 'Please select a marital status.';
      if (!answered(form.gender)) return 'Please select gender / pronouns.';
      if (!answered(form.employmentStatus)) return 'Please select an employment status.';
      if (!answered(form.referralSource)) return 'Please tell us how you heard about us.';
      if (!answered(form.reasonForReachingOut)) return 'Please describe why you are reaching out.';
      if (form.medicationsCurrent && !answered(form.medicationsList))
        return 'Please list your current medications.';
    }
    if (step === null || step === 4) {
      if (!answered(form.consentInformedConsent))    return 'You must acknowledge the informed consent policy.';
      if (!answered(form.consentCancellationPolicy)) return 'You must acknowledge the cancellation policy.';
      if (!answered(form.consentTelehealth))         return 'You must acknowledge the telehealth consent.';
    }
    return '';
  };

  const next = () => {
    const err = validate(step);
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const back = () => { setError(''); setStep(s => s - 1); };

  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSubmitting(true);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.submitClientIntake,
        { method: 'POST', body: JSON.stringify(form) }
      );
      setSubmitted(true);
      await refreshCurrentUser();
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8">Loading saved intake…</div>;
  if (loadError) return <div className="p-8 text-red-700" role="alert">{error}</div>;

  // ─── Success screen ─────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
               style={{ backgroundColor: '#1e562d' }}>
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e562d' }}>
            Thank you, {currentUser?.firstName || 'there'}!
          </h2>
          <p className="text-gray-600 mb-6">
            Your intake information has been saved securely. Your care team will review it and reach out to you shortly.
          </p>
          <p className="text-sm text-gray-400">
            You can update this information at any time by returning to this page.
          </p>
        </div>
      </div>
    );
  }

  const stepProps = { form, handle };

  const stepPanels = [
    <StepPersonal key="personal" {...stepProps} currentUser={currentUser} />,
    <StepEmergency key="emergency" {...stepProps} />,
    <StepInsurance key="insurance" {...stepProps} />,
    <StepClinical key="clinical" {...stepProps} />,
    <StepConsents key="consents" {...stepProps} />,
  ];

  // ─── Layout ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold" style={{ color: '#1e562d' }}>
            Client Intake Form
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Your information is stored securely and never shared with AI systems in identifiable form.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-md p-8">
          <Progress step={step} />

          {/* Step title */}
          <h2 className="text-xl font-semibold mb-6" style={{ color: '#1e562d' }}>
            {STEPS[step]}
          </h2>

          {/* Step content */}
          {stepPanels[step]}

          {/* Error */}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={back}
              disabled={step === 0}
              className="px-5 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Back
            </button>

            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                className="px-6 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: '#1e562d' }}
              >
                Continue →
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                className="px-6 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60"
                style={{ backgroundColor: '#1e562d' }}
              >
                {submitting ? 'Saving…' : 'Submit Intake'}
              </button>
            )}
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Your data is protected under HIPAA. PII is stored separately from clinical information and is never sent to AI systems.
        </p>
      </div>
    </div>
  );
}
